import { useState } from "react";
import { cx } from "../../utils/cx";
import { formatCount as fmt, formatShare } from "./format";

const RADIUS = 40;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
// The surface-coloured gap between slices, in viewBox units
const GAP = 1.5;

/**
 * A donut with its legend. Items are [{ key, label, value, color }]; each item brings its own colour
 * so a status keeps it whatever else is on the chart. Hovering a slice or a legend row shows its
 * count and share in the centre, which otherwise holds the total.
 */
export default function DonutChart({ items, totalLabel = "Total", emptyText = "Nothing recorded for this year" }) {
  const [activeKey, setActiveKey] = useState(null);
  const total = items.reduce((sum, i) => sum + i.value, 0);
  if (!total) return <p className="text-sm text-slate-500">{emptyText}</p>;

  const shown = items.filter((i) => i.value > 0);
  const gap = shown.length > 1 ? GAP : 0;
  let start = 0;
  const slices = shown.map((item) => {
    const length = (item.value / total) * CIRCUMFERENCE;
    // A tiny but non-zero part still shows as a sliver; the legend gives its exact count
    const slice = { ...item, dash: Math.max(length - gap, 1.5), offset: -start };
    start += length;
    return slice;
  });
  const active = items.find((i) => i.key === activeKey);

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
      <div className="relative h-40 w-40 shrink-0">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" role="img" aria-label={items.map((i) => `${i.label}: ${i.value}`).join(", ")}>
          <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="#f1f5f9" strokeWidth="12" />
          {slices.map((s) => (
            <circle
              key={s.key}
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke={s.color}
              strokeWidth={activeKey === s.key ? 14 : 12}
              strokeDasharray={`${s.dash} ${CIRCUMFERENCE - s.dash}`}
              strokeDashoffset={s.offset}
              opacity={activeKey && activeKey !== s.key ? 0.35 : 1}
              className="cursor-pointer transition-all duration-150"
              onMouseEnter={() => setActiveKey(s.key)}
              onMouseLeave={() => setActiveKey(null)}
            />
          ))}
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-2xl font-semibold text-slate-900">{fmt(active ? active.value : total)}</span>
          <span className="max-w-[6.5rem] truncate text-xs text-slate-500">
            {active ? `${active.label} · ${formatShare(active.value, total)}` : totalLabel}
          </span>
        </div>
      </div>

      <ul className="flex w-full min-w-0 flex-col gap-1">
        {items.map((item) => (
          <li
            key={item.key}
            onMouseEnter={() => setActiveKey(item.key)}
            onMouseLeave={() => setActiveKey(null)}
            className={cx(
              "flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm transition-colors",
              activeKey === item.key && "bg-slate-50"
            )}
          >
            <span className="flex min-w-0 items-center gap-2 text-slate-600">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="truncate">{item.label}</span>
            </span>
            <span className="flex shrink-0 items-baseline gap-2">
              <span className="font-semibold text-slate-900">{fmt(item.value)}</span>
              <span className="w-9 text-right text-xs text-slate-400">{formatShare(item.value, total)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
